import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import { isFunction } from '../../helpers/isFunction';
import { useStyles } from '../../hooks/useStyles';
import { useId } from '../../hooks/useId';
import { labeledStyles } from './Labeled.styles';
import { labeledTheme } from './Labeled.stylex';
import { LabeledContext } from './Labeled.context';
import { Stack } from '../Stack';
export const Labeled = forwardRef(function Labeled(props, forwardedRef) {
    const { styles, sx, id: idProp, label, trailingAction, required, supportingText, errorText: errorTextProp, children, hasError: hasErrorProp, labelPosition = 'top', supportingTextPosition: supportingTextPositionProp, errorTextPosition: errorTextPositionProp, disabled, readOnly, loading, requiredSign = '*', ...other } = props;
    const { combineStyles, getStyles, globalStyles } = useStyles({
        name: 'Labeled',
        styles: [labeledStyles, styles],
    });
    const visuallyDisabled = disabled || readOnly || loading;
    const id = useId(idProp);
    const hasError = hasErrorProp && !visuallyDisabled;
    const errorText = hasError ? errorTextProp : undefined;
    const orientation = ['top', 'bottom'].includes(labelPosition)
        ? 'vertical'
        : 'horizontal';
    const isLabelAtStart = ['top', 'left'].includes(labelPosition);
    const supportingTextPosition = labelPosition === 'top'
        ? (supportingTextPositionProp ?? 'start')
        : labelPosition === 'bottom'
            ? (supportingTextPositionProp ?? 'end')
            : labelPosition === 'left'
                ? 'start'
                : 'end';
    const errorTextPosition = errorTextPositionProp ?? supportingTextPosition;
    const hasLeading = (!!label && isLabelAtStart) ||
        ((!!supportingText || !!errorText) &&
            supportingTextPosition === 'start') ||
        (!!errorText && errorTextPosition === 'start');
    const hasTrailing = (!!label && !isLabelAtStart) ||
        ((!!supportingText || !!errorText) && supportingTextPosition === 'end') ||
        (!!errorText && errorTextPosition === 'end');
    const renderLabelAndAction = () => label !== undefined ? (_jsxs("div", { ...getStyles('labelAndActionContainer'), children: [_jsx("div", { ...getStyles('labelContainer'), children: _jsxs("label", { ...getStyles('label', visuallyDisabled
                        ? 'label$disabled'
                        : hasError && !errorText && 'label$error'), htmlFor: id, children: [label, required ? requiredSign : null] }) }), trailingAction ? (_jsx("div", { ...getStyles('action', visuallyDisabled && 'action$disabled'), children: trailingAction })) : null] })) : null;
    const renderSupportingText = () => supportingText ? (_jsx("div", { ...getStyles('supportingText', visuallyDisabled && 'supportingText$disabled'), children: supportingText })) : null;
    const renderErrorText = () => errorText ? (_jsx("div", { ...getStyles('supportingText', 'supportingText$error', visuallyDisabled && 'supportingText$disabled'), children: errorText })) : null;
    const labeledContextValue = useMemo(() => ({
        id,
        required,
        disabled,
        readOnly,
        hasError,
        errorText,
        loading,
    }), [id, required, disabled, readOnly, hasError, errorText, loading]);
    const horizontal = orientation === 'horizontal';
    return (_jsxs(Stack, { ...other, horizontal: horizontal, gap: horizontal ? 3 : 1, sx: [
            labeledTheme,
            globalStyles,
            ...combineStyles(disabled && 'host$disabled'),
            sx,
        ], ref: forwardedRef, children: [hasLeading ? (_jsxs("div", { ...getStyles('rows'), children: [isLabelAtStart ? renderLabelAndAction() : null, supportingTextPosition === 'start' ? renderSupportingText() : null, errorTextPosition === 'start' ? renderErrorText() : null] })) : null, children ? (_jsxs("div", { ...getStyles('content'), children: [_jsx("div", { ...getStyles('element'), children: isFunction(children) ? (children({
                            id,
                            required,
                            disabled,
                            readOnly,
                            loading,
                        })) : (_jsx(LabeledContext.Provider, { value: labeledContextValue, children: children })) }), hasTrailing && isLabelAtStart ? (_jsxs("div", { ...getStyles('rows'), children: [supportingTextPosition === 'end'
                                ? renderSupportingText()
                                : null, errorTextPosition === 'end' ? renderErrorText() : null] })) : null] })) : null, hasTrailing && !isLabelAtStart ? (_jsxs("div", { ...getStyles('rows'), children: [renderLabelAndAction(), supportingTextPosition === 'end' ? renderSupportingText() : null, errorTextPosition === 'end' ? renderErrorText() : null] })) : null] }));
});
